import * as Yup from "yup";
import React, { useEffect, useRef, useState } from "react";
import { FiMail } from "react-icons/fi";
import { Formik } from "formik";
import { MdClose, MdLocalPhone } from "react-icons/md";
import { toast } from "react-toastify";

import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import { cellPhoneNumberRegex } from "utils/regex";

import {
  ContentButtons,
  ContentField,
  ContentToken,
  Field,
  FormContainer,
  ModalContainer,
  ModalHeader,
  ModalStyle,
  TextLabel,
  TextTimer,
} from "./styles";

const formatTimer = (timer: number): string => {
  // @ts-ignore
  let minutes = parseInt(timer / 60, 10);
  // @ts-ignore
  let seconds = parseInt(timer % 60, 10);
  // @ts-ignore
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  // @ts-ignore
  seconds = seconds < 10 ? `0${seconds}` : seconds;
  return `${minutes}:${seconds}`;
};

const ModalValidationContact = (): JSX.Element => {
  const [step, setStep] = useState("validationOption");
  const [show, setShow] = useState(false);
  const [validationType, setValidationType] = useState("");
  const [validationValue, setValidationValue] = useState("");
  const [timer, setTimer] = useState(60);
  const [startTimer, setStartTimer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectTypeValidation, setSelectTypeValidation] = useState("email");

  useEffect(() => {
    (async () => {
      await getContacts();
    })();
  }, []);

  const getContacts = async (): Promise<void> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const response = await api.get(
      `industrial/customer/v1/users/${userId}/contacts?is-contact-validated=true`,
      {
        headers,
      }
    );
    const { data } = response;
    if (data?.length) {
      const isEmailValidated = data.find(
        (contact) => contact.contactType === "EMAIL"
      );
      const isPhoneValidated = data.find(
        (contact) => contact.contactType === "PHONE"
      );
      const isUserClosedModalValidation = sessionStorage.getItem(
        "@UltragazEmpresarial:userClosedModalValidation"
      );

      if (
        (!isEmailValidated || !isPhoneValidated) &&
        !isUserClosedModalValidation
      ) {
        setShow(true);
        setSelectTypeValidation(isEmailValidated ? "phone" : "email");
      }
    }
  };

  const intervalRef = useRef();
  let newTimer = 60;

  const decreaseNum = (): void => {
    if (newTimer === 0) {
      setStartTimer(false);
      clearInterval(intervalRef.current);
      return;
    }
    setTimer((prev) => {
      newTimer -= 1;
      return prev - 1;
    });
  };

  useEffect(() => {
    if (startTimer) {
      // @ts-ignore
      intervalRef.current = setInterval(decreaseNum, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  const sendToken = async (type, value): Promise<void> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const customerSiteId = localStorage.getItem(
      "@UltragazEmpresarial:customerSiteId"
    );
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const bodyContact = {
      contactType: type,
      contact: value,
      document,
      siteId: customerSiteId,
    };

    try {
      const { headers: headersContact } = await api.post(
        `industrial/customer/v1/users/${userId}/contacts`,
        bodyContact,
        { headers }
      );

      const contactId = headersContact?.location?.split("contacts/")[1];

      const bodyToken = {
        contactId,
        tokenType: "REGISTER",
        contact: value,
        contactType: type,
        url: null,
      };

      await api.post(
        `industrial/customer/v1/users/${userId}/tokens`,
        bodyToken,
        {
          headers,
        }
      );

      toast.warn(`Enviamos um código de verificação para ${value}`);
    } catch (error) {
      toast.error(error?.message ?? "Ocorreu um erro interno na aplicação");
    }
  };

  const validationToken = async (token): Promise<void> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const bodyToken = {
      token,
    };

    let success;
    try {
      await api.post(
        `industrial/customer/v1/users/${userId}/validations`,
        bodyToken,
        {
          headers,
        }
      );

      toast.success("Contato validado com sucesso.");
      setShow(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ?? "Ocorreu um erro interno na aplicação"
      );
      success = false;
    }

    return success;
  };

  const handlerSubmitToken = async (data): Promise<void> => {
    setLoading(true);
    await validationToken(data.token);
    setLoading(false);
  };

  const handlerSubmitEmail = async (data): Promise<void> => {
    setLoading(true);
    await sendToken("EMAIL", data.email);
    setStep("validationToken");
    setValidationType("EMAIL");
    setValidationValue(data.email);
    setStartTimer(true);
    setLoading(false);
  };

  const handlerSubmitPhone = async (data): Promise<void> => {
    setLoading(true);
    const value = data.phoneNumber.replace(/[^\d]/g, "");
    await sendToken("PHONE", value);
    setStep("validationToken");
    setValidationType("PHONE");
    setValidationValue(value);
    setStartTimer(true);
    setLoading(false);
  };

  const resetStep = (): void => {
    setStep("validationOption");
    setStartTimer(false);
    setTimer(60);
  };

  const resendToken = async (): Promise<void> => {
    setLoading(true);
    setTimer(60);
    setStartTimer(true);
    await sendToken(validationType, validationValue);
    setLoading(false);
  };

  const onHide = (): void => {
    setShow(false);
    sessionStorage.setItem(
      "@UltragazEmpresarial:userClosedModalValidation",
      "true"
    );
  };

  const validatorSchemaEmail = (): any =>
    Yup.object().shape({
      email: Yup.string()
        .email("E-mail inválido")
        .required("É necessário informar um e-mail"),
    });

  const validatorSchemaToken = (): any =>
    Yup.object().shape({
      token: Yup.string().required("É necessário informar um token"),
    });

  const validatorSchemaPhone = (): any =>
    Yup.object().shape({
      phoneNumber: Yup.string()
        .required("É necessário informar um número de celular")
        .test("Telefone", "Número de telefone inválido", (value) => {
          if (value) {
            value = value.replace(/[^\d]/g, "");
            return !!value.match(cellPhoneNumberRegex);
          }
          return null;
        }),
    });

  return (
    <ModalStyle
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalContainer>
        <ModalHeader>
          <div>
            <div>
              <h2>Valide seus contatos</h2>
              <span>
                Com todos os contatos validados você aproveita tudo que o{" "}
                <b>Portal Minha Ultragaz</b> pode oferecer.
              </span>
            </div>
            <MdClose size={24} onClick={onHide} />
          </div>
        </ModalHeader>

        {step === "validationOption" && (
          <div>
            {selectTypeValidation === "email" && (
              <Formik
                onSubmit={handlerSubmitEmail}
                validationSchema={validatorSchemaEmail}
                initialValues={{
                  email: "",
                }}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <span>Um código será enviado no email informado</span>
                    <ContentField>
                      <Field>
                        <TextField
                          LeadingIcon={<FiMail size={24} />}
                          type="text"
                          name="email"
                          label="Email *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          // @ts-ignore
                          errorCondition={errors.email && touched.email}
                          // @ts-ignore
                          errorMessage={errors.email}
                          disabled={loading}
                        />
                      </Field>
                      <OutlinedButton
                        type="submit"
                        disabled={!values.email || !!errors.email || loading}
                        loading={loading}
                      >
                        Validar email
                      </OutlinedButton>
                    </ContentField>
                  </FormContainer>
                )}
              />
            )}
            {selectTypeValidation === "phone" && (
              <Formik
                onSubmit={handlerSubmitPhone}
                validationSchema={validatorSchemaPhone}
                initialValues={{
                  phoneNumber: "",
                }}
                render={({
                  handleChange,
                  handleBlur,
                  values,
                  errors,
                  touched,
                }) => (
                  <FormContainer>
                    <span>Um código será enviado no celular informado</span>
                    <ContentField>
                      <Field>
                        <TextField
                          LeadingIcon={<MdLocalPhone size={24} />}
                          name="phoneNumber"
                          mask="(99) 9 9999-9999"
                          label="Celular com DDD *"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phoneNumber}
                          // @ts-ignore
                          errorCondition={
                            errors.phoneNumber && !!touched.phoneNumber
                          }
                          // @ts-ignore
                          errorMessage={errors.phoneNumber}
                          disabled={loading}
                        />
                      </Field>
                      <OutlinedButton
                        type="submit"
                        disabled={
                          !values.phoneNumber || !!errors.phoneNumber || loading
                        }
                        loading={loading}
                      >
                        SMS
                      </OutlinedButton>
                    </ContentField>
                  </FormContainer>
                )}
              />
            )}
          </div>
        )}
        {step === "validationToken" && (
          <ContentToken>
            <TextLabel>
              {validationType === "EMAIL" &&
                "O código foi enviado para o email informado"}
              {validationType === "PHONE" && "O código foi enviado por SMS"}
            </TextLabel>
            <Formik
              onSubmit={handlerSubmitToken}
              validationSchema={validatorSchemaToken}
              initialValues={{
                token: "",
              }}
              render={({
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
              }) => (
                <FormContainer>
                  <Field>
                    <TextField
                      name="token"
                      label="Inserir código *"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.token}
                      // @ts-ignore
                      errorCondition={errors.token && touched.token}
                      // @ts-ignore
                      errorMessage={errors.token}
                      disabled={loading}
                    />
                  </Field>
                  <TextTimer>
                    Não recebi o código.{" "}
                    {timer > 0 ? (
                      `Aguarde ${formatTimer(timer)}`
                    ) : (
                      <a href="#" onClick={resendToken}>
                        Enviar novamente
                      </a>
                    )}
                  </TextTimer>
                  <ContentButtons id="content-buttons">
                    <OutlinedButton
                      disabled={!values.token || !!errors.token || loading}
                      type="submit"
                      loading={loading}
                    >
                      Validar código
                    </OutlinedButton>
                    {timer <= 0 && (
                      <OutlinedButton
                        onClick={resetStep}
                        disabled={loading}
                        loading={loading}
                      >
                        Alterar{" "}
                        {validationType === "EMAIL" ? "e-mail" : "celular"}
                      </OutlinedButton>
                    )}
                  </ContentButtons>
                </FormContainer>
              )}
            />
          </ContentToken>
        )}
      </ModalContainer>
    </ModalStyle>
  );
};

export default ModalValidationContact;
